import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { c, fmtDate, rgbaOf, tracked } from '../theme';
import { Body, FieldLabel, Headline, MicroLabel, Notice, Shell, Wordmark } from '../components/Brand';
import { Menu } from '../components/Menu';
import { LoopingVideo } from '../components/LoopingVideo';
import { MinutesDial, ModulesBars, SourcesRing } from '../components/Stat3D';
import { BRAND_GRADIENT, PillField, VioletCTA } from '../components/AuthScene';

/** The sign-in screens' light scrim, so ink reads over the clip. */
const HERO_SCRIM =
  'radial-gradient(70% 55% at 85% 15%, rgba(106,43,217,0.45) 0%, rgba(106,43,217,0) 70%), radial-gradient(60% 45% at 10% 70%, rgba(106,43,217,0.3) 0%, rgba(106,43,217,0) 70%), linear-gradient(180deg,rgba(245,238,219,0.3) 0%,rgba(245,238,219,0.45) 50%,rgba(245,238,219,0.72) 100%)';
import { api, ApiError, type Allowance, type Meta, type ReportSummary, type Suggestion, type User } from '../api';
import { buyReport, isNative, reportPrice } from '../native';

const MIN_QUERY = 4;
const DEBOUNCE_MS = 350;

/** How a finished report reads in the recent list. */
function verdictLook(r: ReportSummary): { tag: string; colour: string } {
  if (r.status === 'assembling') return { tag: 'ASSEMBLING', colour: c.magenta };
  if (r.status === 'failed') return { tag: 'FAILED', colour: c.red };
  // The ReadyBuy Bottom Line leads (reports built before the rename say SNAPSHOT);
  // older labels below stay readable on reports built before it.
  const ten = /^BOTTOM LINE ([\d.]+)\/10$/.exec(r.verdict ?? '');
  if (ten) {
    const n = Number(ten[1]);
    return { tag: `${ten[1]}/10`, colour: n >= 7 ? '#1E8A5A' : n >= 4 ? '#C9741A' : c.red };
  }
  const word = /^BOTTOM LINE \d+ · (PASS|WEAK|STRETCHED|CONDITIONAL|WORKABLE|SUPPORTED|CLEAR)$/.exec(r.verdict ?? '');
  if (word) {
    const w = word[1];
    return { tag: w, colour: w === 'SUPPORTED' || w === 'CLEAR' ? c.cyan : w === 'WORKABLE' ? c.violet : w === 'PASS' || w === 'WEAK' ? c.red : c.magenta };
  }
  const snap = /^(?:BOTTOM LINE|SNAPSHOT) (\d+) · (STRONG|FAIR|WEAK)$/.exec(r.verdict ?? '');
  if (snap) return { tag: `BOTTOM LINE ${snap[1]}`, colour: snap[2] === 'STRONG' ? c.cyan : snap[2] === 'FAIR' ? c.violet : c.red };
  if (r.verdict === 'STRONG VALUE') return { tag: 'STRONG VALUE', colour: c.cyan };
  if (r.verdict === 'FAIR PRICE') return { tag: 'FAIR PRICE', colour: c.violet };
  if (r.verdict === 'PRICED HIGH') return { tag: 'PRICED HIGH', colour: c.magenta };
  if (r.verdict === 'OVERPRICED') return { tag: 'OVERPRICED', colour: c.red };
  if (r.verdict === 'OFFER GUIDE') return { tag: 'OFFER GUIDE', colour: c.violet };
  if (r.verdict === 'NOT RATED') return { tag: 'NOT RATED', colour: c.ink45 };
  if (r.verdict === 'PROCEED') return { tag: 'PROCEED', colour: c.cyan };
  if (r.verdict === 'PROCEED WITH CONDITIONS') return { tag: 'CONDITIONS', colour: c.violet };
  if (r.verdict === 'HOLD') return { tag: 'HOLD', colour: c.red };
  return { tag: 'NO WINDOW', colour: c.ink45 };
}

export function Search({ user, reports, onAllowance }: { user: User | null; reports: ReportSummary[]; onAllowance?: (a: Allowance) => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Suggestion[]>([]);
  const [picked, setPicked] = useState<Suggestion | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const navigate = useNavigate();
  const abort = useRef<AbortController | null>(null);
  // In the iOS app, a report is bought through Apple when none are left.
  const [price, setPrice] = useState<string | null>(null);
  const [buying, setBuying] = useState(false);
  const [buyNote, setBuyNote] = useState<string | null>(null);
  useEffect(() => {
    void reportPrice().then(setPrice);
  }, []);
  const buy = async () => {
    if (!user || buying) return;
    setBuying(true);
    setBuyNote(null);
    try {
      const r = await buyReport(user.id, api.storePurchase);
      if (r.status === 'purchased') onAllowance?.(r.result.allowance);
      else if (r.status === 'pending') setBuyNote('Your purchase is waiting for approval. The report will be added as soon as it goes through.');
    } catch (err) {
      setBuyNote(err instanceof ApiError ? err.message : 'The purchase did not go through. You have not been charged twice; try again in a moment.');
    } finally {
      setBuying(false);
    }
  };

  useEffect(() => {
    let live = true;
    api
      .meta()
      .then((m) => live && setMeta(m))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  // Every keystroke past the threshold is a metered call, so it is debounced
  // and the in-flight request is cancelled before another goes out.
  useEffect(() => {
    const q = query.trim();
    if (picked && q === picked.line1) return;
    if (q.length < MIN_QUERY) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const timer = setTimeout(async () => {
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;
      try {
        const { results: hits } = await api.search(q, ctrl.signal);
        setResults(hits);
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof ApiError ? err.message : 'Address lookup is unavailable right now.');
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, picked]);

  // The address is the first of three steps now: the buyer-fit assessment is
  // asked before anything is bought, so nobody pays for a report and is then
  // asked what they wanted from it. The report itself is created at the end of
  // that screen, which is also where the pick would be lost on a refresh —
  // hence history state rather than a route parameter.
  const assemble = () => {
    if (!picked) return;
    navigate('/fit', { state: { gnafId: picked.gnafId, displayName: picked.displayName } });
  };

  const hint = (() => {
    if (query.trim().length < MIN_QUERY) return 'TYPE AT LEAST 4 CHARACTERS';
    if (searching) return 'SEARCHING ADDRESSES…';
    if (results.length === 0) return 'NO MATCHES · KEEP TYPING';
    return `${results.length} ${results.length === 1 ? 'MATCH' : 'MATCHES'}`;
  })();

  const assembling = reports.find((r) => r.status === 'assembling') ?? null;
  const latestReady = reports.find((r) => r.status === 'ready' && !r.archived) ?? null;
  const recent = reports.filter((r) => !r.archived).slice(0, 3);

  // Beta testers get a set number of reports. Counted from the list on screen,
  // so it is current the moment a report starts; the server enforces it too.
  const limit = user?.allowance?.limit ?? null;
  // The server's count also includes reports since deleted, which still count.
  const used = Math.max(user?.allowance?.used ?? 0, reports.filter((r) => r.status !== 'failed').length);
  const left = limit == null ? null : Math.max(0, limit - used);
  const usedUp = left === 0;

  return (
    <Shell padded={false} style={{ borderRadius: 40 }}>
      {/* 1–4 — The search itself, over the onboarding clip behind the sign-in screens' frosted scrim */}
      <div style={{ position: 'relative', paddingBottom: 36, isolation: 'isolate' }}>
        <LoopingVideo src="/onboarding-bg.mp4" fallback={BRAND_GRADIENT} />
        <div aria-hidden style={{ position: 'absolute', inset: 0, background: HERO_SCRIM, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }} />
        <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '48px var(--gx) 0' }}>
        <Wordmark />
        <Menu
          current="search"
          user={user}
          reportCount={reports.length}
          currentReport={latestReady ? { id: latestReady.id, address: latestReady.address } : null}
          assembling={assembling ? { id: assembling.id, address: assembling.address } : null}
        />
      </div>

      {/* 2 — Intro */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '56px var(--gx) 0' }}>
        <MicroLabel>NEW REPORT</MicroLabel>
        <Headline>
          {/* One line; the size steps down on narrow phones so it never wraps. */}
          <span style={{ whiteSpace: 'nowrap', fontSize: 'clamp(30px, 9.5vw, 40px)' }}>
            Which property<span style={{ color: c.red }}>?</span>
          </span>
        </Headline>
        <Body>
          Enter the address and we’ll pull {meta?.modules ?? 21} modules of live property data. Takes a
          couple of minutes.
        </Body>
      </div>

      {/* For-sale homes carry the asking price, listing details and agent record the window leans on */}
      <div style={{ padding: '24px var(--gx) 0' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 18px', borderRadius: 22, background: 'rgba(245,238,219,0.72)', border: '1px solid rgba(245,238,219,0.9)' }}>
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: c.red, marginTop: 6, flex: 'none' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 700, color: c.red }}>FOR THE MOST ACCURATE REPORT</div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              Search a home that is currently for sale. Its asking price, listing details and the agent’s track record all feed your report. A home that isn’t on the market still gets a report, but with less to go on, so its figures are less precise.
            </div>
          </div>
        </div>
      </div>

      {/* Beta allowance: said before the address, so nobody spends it by surprise */}
      {left != null && (
        <div style={{ padding: '24px var(--gx) 0' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 18px', borderRadius: 22, background: 'rgba(245,238,219,0.72)', border: '1px solid rgba(245,238,219,0.9)' }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: usedUp ? c.red : c.violet, marginTop: 6, flex: 'none' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 700, color: usedUp ? c.red : c.violet }}>
                {isNative
                  ? usedUp
                    ? 'NO REPORTS LEFT'
                    : `${left} ${left === 1 ? 'REPORT' : 'REPORTS'} LEFT`
                  : usedUp
                    ? 'NO REPORTS LEFT'
                    : `BETA ACCESS · ${left} ${left === 1 ? 'REPORT' : 'REPORTS'} LEFT`}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                {isNative
                  ? usedUp
                    ? 'One report covers one property: the offer window, price rating and every module, yours to keep on this account.'
                    : 'Once a report starts, it counts. If it fails to build, you get it back.'
                  : usedUp
                  ? 'Reports are bought in the ReadyBuy app. One report covers one property: the offer window, price rating and every module, yours to keep on this account.'
                  : limit === 1
                    ? 'Your beta access includes 1 report. Choose the property you’re most serious about: once a report starts, it counts. If it fails to build, you get it back.'
                    : `Your beta access includes ${limit} reports. Once a report starts, it counts. If it fails to build, you get it back.`}
              </div>
              {usedUp && isNative && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                  <VioletCTA disabled={buying || !price} onClick={() => void buy()}>
                    {buying ? 'buying…' : price ? `buy a report · ${price}` : 'loading price…'}
                  </VioletCTA>
                  {buyNote && <div style={{ fontSize: 12, lineHeight: 1.5, color: c.red }}>{buyNote}</div>}
                </div>
              )}
              {usedUp && !isNative && (
                <a href="https://readybuy.au" target="_blank" rel="noreferrer" style={{ fontSize: 11, letterSpacing: '0.14em', fontWeight: 700, color: c.violet, textDecoration: 'none', marginTop: 4 }}>
                  GET THE APP ›
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {!usedUp && (<>
      {/* 3 — Address field */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '40px var(--gx) 0' }}>
        <FieldLabel>ADDRESS</FieldLabel>
        <PillField
          dot={c.violet}
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="Start typing an address…"
          aria-label="Address"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPicked(null);
          }}
          trailing={
            <span aria-hidden style={{ width: 46, height: 46, flex: 'none', borderRadius: '50%', background: c.violet, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="7" r="5" stroke={c.cream} strokeWidth="1.6" />
                <path d="M11 11l3.5 3.5" stroke={c.cream} strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </span>
          }
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 4px 0',
            fontSize: 10,
            letterSpacing: '0.12em',
            fontWeight: 600,
            color: c.ink6,
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: c.red,
              animation: 'pulseDot 1.4s ease-in-out infinite',
            }}
          />
          {hint}
        </div>
      </div>

      {/* 4 — Suggestions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '14px 32px 0' }}>
        {error && <Notice tone="error">{error}</Notice>}
        {!picked && results.map((s) => {
          // The list only shows before a pick; the chosen address moves to the card below.
          const tag = s.propertyType === 'unit' ? 'UNIT' : '';
          return (
            <button
              key={s.gnafId}
              className="rb-frost-tile"
              onClick={() => {
                setPicked(s);
                setQuery(s.line1);
              }}
              style={{
                display: 'grid',
                gridTemplateColumns: '26px 1fr auto',
                alignItems: 'center',
                gap: 14,
                padding: '13px 14px',
                borderRadius: 20,
                background: 'rgba(245,238,219,0.62)',
                border: '1px solid rgba(245,238,219,0.8)',
                color: c.ink,
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: 'rgba(106,43,217,0.14)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: c.violet,
                  }}
                />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 500 }}>{s.line1}</span>
                <span style={{ fontSize: 11, color: c.ink5 }}>{s.line2}</span>
              </span>
              <span
                style={{
                  fontSize: 9,
                  letterSpacing: '0.14em',
                  fontWeight: 600,
                  color: c.ink45,
                  whiteSpace: 'nowrap',
                }}
              >
                {tag}
              </span>
            </button>
          );
        })}
      </div>

      {/* 4b — The selection and the CTA, straight under the field: once an
          address is picked the suggestions fold away, so CONTINUE is in view
          without scrolling past the stats and recent reports. */}
      {picked && (
        <div style={{ padding: '14px 32px 0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 4 }}>
            {picked && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  padding: '16px 20px',
                  borderRadius: 22,
                  background: 'rgba(245,238,219,0.72)',
                  border: '1px solid rgba(245,238,219,0.9)',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                  <div style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 600, color: c.red }}>SELECTED</div>
                  <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>{picked.displayName}</div>
                </div>
                <div
                  style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 600, color: c.ink5, whiteSpace: 'nowrap' }}
                >
                  ADDRESS MATCH
                </div>
              </div>
            )}

            <VioletCTA onClick={assemble} disabled={!picked}>
              continue
            </VioletCTA>
          </div>
        </div>
      )}
      </>)}

        </div>
      </div>

      {/* 5 — Stat strip, as a section card */}
      <div style={{ margin: '8px 8px 0', borderRadius: 32, overflow: 'hidden', background: c.section1 }}>
        <StatStrip meta={meta} />
      </div>

      {/* 6 — Recent reports */}
      <div
        style={{
          background: c.section2,
          margin: '8px 8px 8px',
          borderRadius: 32,
          padding: '34px 16px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 22,
          // The last block on the screen, so it carries any leftover height.
          flex: 1,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '0 16px' }}>
          <div style={{ fontSize: 24, letterSpacing: '0.01em', whiteSpace: 'nowrap', fontWeight: 500 }}>
            <span style={{ fontWeight: 700 }}>{recent.length}</span> Recent
          </div>
          <button
            onClick={() => navigate('/portfolio')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 9, letterSpacing: '0.14em', fontWeight: 700, color: c.ink, whiteSpace: 'nowrap' }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.red, animation: 'throb 2.4s ease-in-out infinite' }} />
            ALL REPORTS
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {recent.length === 0 && (
            <div style={{ fontSize: 11, color: c.ink5, lineHeight: 1.55, padding: '0 16px' }}>
              Nothing yet — your reports will appear here.
            </div>
          )}
          {recent.map((r, i) => {
            const look = verdictLook(r);
            return (
              <button
                key={r.id}
                className="rb-tile"
                onClick={() => navigate(r.status === 'ready' ? `/report/${r.id}` : `/assembling/${r.id}`)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '26px 1fr auto',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 14px',
                  borderRadius: 20,
                  background: c.tint,
                  color: c.ink,
                  textAlign: 'left',
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: rgbaOf(look.colour.startsWith('#') ? look.colour : '#1F1A1F', 0.16),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: look.colour, animation: 'throb 2.4s ease-in-out infinite', animationDelay: `${(i * 0.18).toFixed(2)}s` }} />
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 500 }}>{r.line1}</span>
                  <span
                    style={{
                      fontSize: 11,
                      color: c.ink5,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.locality} · {fmtDate(r.completedAt ?? r.createdAt)}
                  </span>
                </span>
                <span
                  style={{
                    fontSize: 8,
                    letterSpacing: '0.1em',
                    fontWeight: 700,
                    color: look.colour,
                    whiteSpace: 'nowrap',
                    padding: '5px 8px',
                    borderRadius: 9,
                    border: `1.5px solid ${rgbaOf(look.colour.startsWith('#') ? look.colour : '#1F1A1F', 0.2)}`,
                  }}
                >
                  {look.tag}
                </span>
              </button>
            );
          })}
        </div>

      </div>
    </Shell>
  );
}

/** SOURCES · MODULES · MINUTES, each with its own small 3D piece in the ring's style. */
function StatStrip({ meta }: { meta: Meta | null }) {
  const cell = (last?: boolean): React.CSSProperties => ({
    padding: '30px 4px 32px',
    minWidth: 0,
    borderRight: last ? undefined : `1px solid ${c.hairline}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
  });
  const label: React.CSSProperties = {
    fontSize: 10,
    color: c.red,
    fontWeight: 600,
    textAlign: 'center',
    ...tracked(0.16),
  };
  const value: React.CSSProperties = { fontSize: 22, lineHeight: 1, fontWeight: 600, letterSpacing: '-0.01em' };

  return (
    <div
      style={{
        display: 'grid',
        // minmax(0, 1fr): the 92px graphics must not push a column wider than a third.
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        padding: '0 6px',
      }}
    >
      <div style={cell()}>
        <div style={label}>SOURCES</div>
        <div style={value}>{meta?.sources ?? '—'}</div>
        <SourcesRing n={meta?.sources ?? 24} />
      </div>

      <div style={cell()}>
        <div style={label}>MODULES</div>
        <div style={value}>{meta?.modules ?? '—'}</div>
        <ModulesBars n={meta?.modules ?? 22} />
      </div>

      <div style={cell(true)}>
        <div style={label}>MINUTES</div>
        <div style={value}>~{meta?.estimatedMinutes ?? 2}</div>
        <MinutesDial minutes={meta?.estimatedMinutes ?? 2} />
      </div>
    </div>
  );
}
